import React, { useContext, useEffect, useRef } from 'react';
import { AppContext, DispatchContext } from '../AppContext';
import { Route, Switch, useHistory, useLocation } from 'react-router-dom';
import { makeStyles } from "@material-ui/core/styles";
import CircularProgress from '@material-ui/core/CircularProgress';

import AppGrid from './AppGrid';
import Login from '../containers/Login/Login';
import Register from '../components/Register';
import Quote from '../components/Quote';
import Shop from '../components/Shop';
import Maintenance from '../components/Maintenance';
import Contact from '../components/Contact';
import RevokeContractPage from '../components/RevokeContractPage';
import ResetPassword from '../components/ResetPassword';
import Libtext from '../containers/Libtext/Libtext';
import PermaLink from '../containers/PermaLink/PermaLink';
import Checkout from '../containers/Checkout';
import * as actions from '../actions/actions';
import Service from "../services/service";
import { getBrowserFingerprint, getCookie, setCookie, config } from '../helpers/constants';
import { ensureCsrfToken } from '../helpers/api';
import ToastrComponent from '../helpers/toastrComponent';
import FullScreenLoading from '../components/FullScreenLoading';
import UploadForm from '../components/UploadForm';
import WizardPanel from '../components/WizardPanel';


export default function AppRouter(props) {

    const useStyles = makeStyles((theme) => ({
        root: {
            display: "flex",
            flexDirection: "column",
            width: "100%",
            height: "100vh",
            background: theme.palette.background.image,
            backgroundSize: "cover",
            padding: theme.spacing(2),
            justifyContent: "center",
            alignItems: "center",
            color: "white"
        }
    }));

    const dispatch = useContext(DispatchContext);
    const state = useContext(AppContext);
    const classes = useStyles();
    const initRef = useRef(false);
    const history = useHistory();
    const location = useLocation();

    const cookieSessionId = getCookie(config.sessionCookieName);

    // When the user navigates to an external page (e.g. Mollie checkout) and then presses
    // the browser "back" button, the browser may restore this page from the back-forward
    // cache (bfcache) with the full JS heap intact — including a Redux state that still has
    // fullScreenLoading=true. The pageshow event fires on bfcache restores (event.persisted)
    // so we can reliably clear the spinner here without causing flicker on the outward journey.
    // If an invoiceId was stored before the Mollie redirect, navigate to /checkout/{id} so the
    // user sees the payment result instead of the order-creation screen (which would trigger a
    // new order if they clicked Next again).
    useEffect(() => {
        const handlePageShow = (event) => {
            if (event.persisted) {
                dispatch(actions.showFullscreenLoading(false));
                const invoiceId = sessionStorage.getItem('mollieCheckoutInvoiceId');
                if (invoiceId) {
                    sessionStorage.removeItem('mollieCheckoutInvoiceId');
                    sessionStorage.setItem('mollieCheckoutBackNav', '1');
                    dispatch(actions.navigate_to('/checkout/' + invoiceId));
                }
            }
        };
        window.addEventListener('pageshow', handlePageShow);
        return () => window.removeEventListener('pageshow', handlePageShow);
    }, [dispatch]); // eslint-disable-line react-hooks/exhaustive-deps

    // Initial app initialization: fetch CSRF token (which also updates the
    // session cookie if the server assigned a new session), then getUserInfo.
    useEffect(() => {
        if (initRef.current) return;
        if (state.app.maintenance) return;
        if (state.user.sessionId && state.user.sessionId === cookieSessionId) return;

        initRef.current = true;
        ensureCsrfToken(config.backendUrl + "api/").then(() => {
            Service.getUserInfo().then(response => {
                if (response && response.success) {
                    setCookie(config.sessionCookieName, response.data.sessionId, 365);
                    if (response.data.sessionId != getCookie(config.sessionCookieName)) {
                        alert("Unable to set session cookie. Please make sure to allow cookies in your browser settings.");
                        return null;
                    }
                    dispatch(actions.setUserDetails(response.data));
                } else {
                    const isBlocked = (response && response.httpStatus === 403);
                    dispatch(actions.setMaintenance(isBlocked ? 'blocked' : 'maintenance'));
                }
            }).catch(() => {
                dispatch(actions.setMaintenance('maintenance'));
            });
        }).catch(() => {
            dispatch(actions.setMaintenance('maintenance'));
        });
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (state.user.sessionId) {
            loadNews();
            // Also handle the case where the user pressed back from Mollie but bfcache
            // was disabled (e.g. Cache-Control: no-store in PROD), causing a full page
            // reload instead of a bfcache restore. The pageshow handler covers the
            // bfcache path; this covers the fresh-reload path.
            const invoiceId = sessionStorage.getItem('mollieCheckoutInvoiceId');
            if (invoiceId) {
                sessionStorage.removeItem('mollieCheckoutInvoiceId');
                sessionStorage.setItem('mollieCheckoutBackNav', '1');
                dispatch(actions.navigate_to('/checkout/' + invoiceId));
            }
        }
    }, [state.lang, state.user.sessionId]);

    // Handle ?action=revokeContract: open profile dialog at Order History tab if logged in,
    // otherwise redirect to login page preserving the action param.
    useEffect(() => {
        const params = new URLSearchParams(location.search);
        if (params.get('action') === 'revokeContract' && !location.pathname.startsWith('/login')) {
            if (state.user.sessionId) {
                if (state.user.loggedIn) {
                    // Strip the query param from the URL, then open the dialog
                    history.replace({ search: '' });
                    dispatch(actions.setUI({ profileDialogVisible: true, profileDialogTab: 2, profileDialogWithdrawHint: true }));
                } else if (location.pathname !== '/') {
                    history.replace({ search: '' });
                    dispatch(actions.navigate_to('/revoke'));
                }
            }
        }
    }, [state.user.sessionId, state.user.loggedIn, location.pathname, location.search]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        // Only fetch products/countries/browserId after user session is established
        if (!state.user.sessionId) return;
        Service.setBrowserId(getBrowserFingerprint());
        Service.getProducts().then(response => {
            if (response && response.success) {
                dispatch(actions.setCheckoutDetails({ products: response.data }));
            }
        }).catch(() => {});
        Service.getCountries().then(response => {
            if (response && response.success) {
                dispatch(actions.setCheckoutDetails({ countries: response.data }));
            }
        }).catch(() => {});
    }, [state.user.sessionId]);

    function loadNews() {
        Service.loadNews().then(res => {
            if (res.success) {
                dispatch(actions.get_news(res || []))
            }
        }).catch(() => {});
    }


    useEffect(() => {
        if (state.ui.redirect) {
            const pageName = state.ui.pageName;
            dispatch(actions.reset_navigate());
            if (pageName.substring(0, 4) === "http") {
                window.location.href = pageName;
            } else {
                history.push(pageName);
            }
        }
    }, [state.ui.redirect, state.ui.pageName]); // eslint-disable-line react-hooks/exhaustive-deps


    // Show loading spinner while waiting for initial session
    if (!state.app.maintenance && !state.user.sessionId) {
        return (
            <div className={classes.root}>
                <CircularProgress color="inherit" />
            </div>
        );
    }

    if (state.app.maintenance || (process.env.REACT_APP_MAINTENANCE == "true")) {
        return (<Maintenance />);
    }

    return (
        <div>
            <ToastrComponent />
            {state.ui.fullScreenLoading && <FullScreenLoading visible={state.ui.fullScreenLoading} text={state.ui.fullScreenLoadingText} />}

            <Switch>
                <Route exact path="/login/:sso?" component={Login} />
                <Route exact path="/revoke" component={RevokeContractPage} />
                <Route exact path="/quote" component={Quote} />
                <Route exact path="/shop/:prodId?/:origin?" component={Shop} />
                <Route exact path="/wizard" component={WizardPanel} />
                <Route exact path="/contact" component={Contact} />
                <Route exact path="/signup/:lc?" component={Register} />
                <Route exact path="/reset" component={ResetPassword} />
                <Route path="/libtext/:id/:type?" component={Libtext} />
                <Route path="/permalink/:id/:action?" component={PermaLink} />
                <Route path="/upload/:id" component={UploadForm} />
                <Route path="/checkout/:id" component={Checkout} />
                <Route path="/" component={AppGrid} />
            </Switch>
        </div>
    );
}
